
import React, { useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { MoreHorizontal, Eye, Edit, Trash2, MessageSquare, Clock, Loader2 } from 'lucide-react';
import { Lead } from '@/hooks/useLeadsData';
import { ScheduleMessageModal } from '@/components/scheduled-messages/ScheduleMessageModal';
import { formatarTelefone } from './phoneUtils';
import { ContactsMobileList } from './ContactsMobileList';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { ThinkingOrbLoader } from '@/components/ui/ThinkingOrbLoader';

/**
 * Componente de tabela para exibir contatos/leads - VERSÃO OTIMIZADA
 * 
 * MELHORIAS:
 * - Melhor feedback visual para carregamento infinito
 * - Contador de contatos mais preciso
 * - Estados de carregamento mais informativos
 * - Integração com ScheduleMessageModal
 * - Suporte responsivo com visualização de Cards em mobile
 */

// Função para obter ícone da origem do lead
const getOriginIcon = (origem: string | null | undefined) => {
  if (!origem) return null;

  const origemLower = origem.toLowerCase();
  if (origemLower.includes('whatsapp')) {
    return <MessageSquare className="w-4 h-4 text-green-600" />;
  }

  if (origemLower.includes('instagram')) {
    return <MessageSquare className="w-4 h-4 text-purple-600" />;
  }

  return <MessageSquare className="w-4 h-4 text-gray-600" />;
};

interface ContactsTableProps {
  leads: Lead[];
  selectedLeads: string[];
  onLeadSelect: (leadId: string) => void;
  onSelectAll: (checked: boolean) => void;
  onViewLead: (lead: Lead) => void;
  onEditLead: (lead: Lead) => void;
  onDeleteLead: (leadId: string) => void;
  onOpenChat: (lead: Lead) => void; // Nova função para abrir chat
  isAllSelected: boolean;
  // Props para carregamento infinito otimizado
  hasMoreLeads?: boolean;
  remainingLeadsCount?: number;
  isLoadingMore?: boolean;
  onLoadMore?: () => void;
  totalLeads?: number;
  displayedLeadsCount?: number;
  // Estados de erro
  isError?: boolean;
  error?: Error | null;
}

export const ContactsTable: React.FC<ContactsTableProps> = ({
  leads,
  selectedLeads,
  onLeadSelect,
  onSelectAll,
  onViewLead,
  onEditLead,
  onDeleteLead,
  onOpenChat, // Nova prop para abrir chat
  isAllSelected,
  // Props para carregamento infinito
  hasMoreLeads = false,
  remainingLeadsCount = 0,
  isLoadingMore = false,
  onLoadMore,
  totalLeads = 0,
  displayedLeadsCount = 0,
  // Estados de erro
  isError = false,
  error = null
}) => {
  // Estado para controlar qual modal está aberto
  const [scheduleModalLead, setScheduleModalLead] = useState<Lead | null>(null);

  // Hook para detectar mobile
  const isMobile = useMediaQuery('(max-width: 768px)');

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('pt-BR');
  };

  const getStatusBadge = (lead: Lead) => {
    if (lead.convertido) {
      return <Badge className="bg-green-100 text-green-800">Convertido</Badge>;
    }
    return <Badge variant="secondary">Em andamento</Badge>;
  };

  // Função para abrir modal de programar mensagem
  const handleScheduleMessage = (lead: Lead) => {
    setScheduleModalLead(lead);
  };

  // Se houver erro, exibir estado de erro
  if (isError) {
    return (
      <div className="rounded-md border border-red-200 bg-red-50 p-8">
        <div className="text-center">
          <div className="w-12 h-12 mx-auto mb-4 bg-red-100 rounded-full flex items-center justify-center">
            <svg className="w-6 h-6 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-red-900 mb-2">
            Erro ao carregar contatos
          </h3>
          <p className="text-red-700 mb-4">
            {error?.message || 'Ocorreu um erro inesperado ao buscar os contatos.'}
          </p>
          <Button
            variant="outline"
            onClick={() => window.location.reload()}
            className="border-red-300 text-red-700 hover:bg-red-100"
          >
            Tentar novamente
          </Button>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Cabeçalho aprimorado com informações de carregamento */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-4">
          <div className="text-sm text-gray-600">
            {totalLeads > 0 ? (
              <div className="flex items-center gap-2">
                <span>
                  Exibindo <span className="font-medium text-green-600">{displayedLeadsCount}</span> de{' '}
                  <span className="font-medium">{totalLeads}</span> contatos
                </span>

                {/* Indicador de busca ativa - Ocultar em mobile muito pequeno para evitar quebra */}
                {totalLeads !== displayedLeadsCount && (
                  <div className="hidden sm:inline-flex items-center px-2 py-1 rounded-full text-xs bg-blue-100 text-blue-700">
                    <div className="w-1.5 h-1.5 bg-blue-400 rounded-full mr-1.5"></div>
                    {remainingLeadsCount} disponíveis
                  </div>
                )}
              </div>
            ) : (
              <span>Nenhum contato encontrado</span>
            )}
          </div>
        </div>
      </div>

      {/* Renderização Condicional: Mobile List vs Desktop Table */}
      {isMobile ? (
        <ContactsMobileList
          leads={leads}
          onViewLead={onViewLead}
          onEditLead={onEditLead}
          onDeleteLead={onDeleteLead}
          onOpenChat={onOpenChat}
          onScheduleMessage={handleScheduleMessage}
        />
      ) : (
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-12">
                  <Checkbox
                    checked={isAllSelected}
                    onCheckedChange={onSelectAll}
                  />
                </TableHead>
                <TableHead>Nome</TableHead>
                <TableHead>Contato</TableHead>
                <TableHead>Origem</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Criado em</TableHead>
                <TableHead>Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {leads.map((lead) => (
                <TableRow key={lead.id}>
                  <TableCell>
                    <Checkbox
                      checked={selectedLeads.includes(lead.id)}
                      onCheckedChange={() => onLeadSelect(lead.id)}
                    />
                  </TableCell>
                  <TableCell className="font-medium">
                    <div>
                      <div>{lead.nome || 'Lead sem nome'}</div>
                      {lead.email && (
                        <div className="text-sm text-gray-500">{lead.email}</div>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      {lead.telefone && (
                        <span className="text-sm">{formatarTelefone(lead.telefone)}</span>
                      )}
                      {!lead.telefone && lead.origem_lead?.toLowerCase().includes('instagram') && (
                        <span className="text-sm text-purple-600">Instagram DM</span>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      {getOriginIcon(lead.origem_lead)}
                      <span className="text-sm">{lead.origem_lead || 'N/A'}</span>
                    </div>
                  </TableCell>
                  <TableCell>{getStatusBadge(lead)}</TableCell>
                  <TableCell>{formatDate(lead.created_at)}</TableCell>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      {/* Botão Abrir Chat - visível */}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => onOpenChat(lead)}
                        className="h-8 px-3 text-green-600 border-green-200 hover:bg-green-50"
                      >
                        <MessageSquare className="h-4 w-4 mr-1" />
                        Abrir Chat
                      </Button>

                      {/* Menu de ações adicionais */}
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" className="h-8 w-8 p-0">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => onViewLead(lead)}>
                            <Eye className="mr-2 h-4 w-4" />
                            Visualizar
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => onEditLead(lead)}>
                            <Edit className="mr-2 h-4 w-4" />
                            Editar
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => handleScheduleMessage(lead)}
                            className="text-blue-600"
                          >
                            <Clock className="mr-2 h-4 w-4" />
                            Programar Mensagem
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            onClick={() => onDeleteLead(lead.id)}
                            className="text-red-600"
                          >
                            <Trash2 className="mr-2 h-4 w-4" />
                            Excluir
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      {/* Seção de carregamento infinito aprimorada - Mobile Friendly */}
      {hasMoreLeads && onLoadMore && (
        <div className="mt-6 flex flex-col items-center gap-4 pb-20 sm:pb-0"> {/* Padding bottom extra em mobile */}
          {/* Botão Carregar Mais aprimorado */}
          <Button
            variant="outline"
            onClick={onLoadMore}
            disabled={isLoadingMore}
            className="flex items-center gap-2 px-8 py-3 rounded-2xl border-green-200 hover:bg-green-50 hover:border-green-300 hover:shadow-md transition-all duration-200 text-green-700 font-medium w-full sm:w-auto justify-center" // w-full em mobile
          >
            {isLoadingMore ? (
              <>
                <ThinkingOrbLoader state="working" size={20} />
                Carregando corre...
              </>
            ) : (
              <>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
                </svg>
                Carregar mais
              </>
            )}
          </Button>

          {/* Informações detalhadas */}
          <div className="text-center w-full max-w-xs">
            <div className="text-sm text-gray-600 mb-1">
              <span className="font-medium">{remainingLeadsCount}</span> contatos restantes
            </div>

            {/* Barra de progresso visual */}
            <div className="w-full h-1.5 bg-gray-200 rounded-full overflow-hidden">
              <div
                className="h-full bg-gradient-to-r from-green-400 to-green-500 transition-all duration-300"
                style={{
                  width: `${Math.min(100, (displayedLeadsCount / totalLeads) * 100)}%`
                }}
              />
            </div>

            <div className="text-xs text-gray-500 mt-1">
              {Math.round((displayedLeadsCount / totalLeads) * 100)}% carregado
            </div>
          </div>
        </div>
      )}

      {/* Estado quando todos os contatos foram carregados */}
      {!hasMoreLeads && totalLeads > 0 && displayedLeadsCount >= totalLeads && (
        <div className="mt-6 text-center pb-20 sm:pb-0">
          <div className="inline-flex items-center gap-2 text-sm text-green-600 font-medium bg-green-50 px-4 py-2 rounded-full border border-green-200">
            <div className="w-2 h-2 bg-green-400 rounded-full"></div>
            Todos os contatos carregados
          </div>
        </div>
      )}

      {/* Estado de carregamento inicial */}
      {isLoadingMore && leads.length === 0 && (
        <div className="mt-6 text-center">
          <div className="inline-flex items-center gap-2 text-sm text-gray-600">
            <Loader2 className="h-4 w-4 animate-spin" />
            Buscando contatos...
          </div>
        </div>
      )}

      {/* Modal de Programar Mensagem */}
      <ScheduleMessageModal
        isOpen={!!scheduleModalLead}
        onClose={() => setScheduleModalLead(null)}
        selectedLead={scheduleModalLead}
      />
    </>
  );
};
